import {Box, Divider, Fade, Paper, SxProps, Theme, Tooltip, TooltipProps, tooltipClasses, Typography} from "@mui/material";
import {alpha} from "@mui/material/styles";
import * as React from "react";
import {directionColor, signed} from "./insightsFormat";

const MAX_WIDTH = 240;
const GAP = "6px";

const panelSx = (theme: Theme) => ({
    px: 1,
    py: 0.75,
    border: "1px solid",
    borderColor: alpha(theme.palette.text.primary, 0.14),
    borderRadius: "8px",
    bgcolor: "background.paper",
    color: "text.primary",
    fontSize: 13,
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.10)"
});

export function InsightsTooltip({
                                    children,
                                    ...props
                                }: Omit<TooltipProps, "arrow" | "slotProps" | "TransitionComponent">) {
    return (
        <Tooltip
            {...props}
            TransitionComponent={Fade}
            slotProps={{
                tooltip: {
                    sx: theme => ({
                        ...panelSx(theme),
                        maxWidth: MAX_WIDTH,
                        [`.${tooltipClasses.popper}[data-popper-placement*="top"] &`]: {marginBottom: GAP},
                        [`.${tooltipClasses.popper}[data-popper-placement*="bottom"] &`]: {marginTop: GAP},
                        [`.${tooltipClasses.popper}[data-popper-placement*="left"] &`]: {marginRight: GAP},
                        [`.${tooltipClasses.popper}[data-popper-placement*="right"] &`]: {marginLeft: GAP}
                    })
                }
            }}
        >
            {children}
        </Tooltip>
    );
}

interface InsightsTooltipPanelProps {
    minWidth?: number;
    sx?: SxProps<Theme>;
    children: React.ReactNode;
}

export function InsightsTooltipPanel({minWidth = 240, sx, children}: InsightsTooltipPanelProps) {
    return (
        <Paper
            elevation={0}
            sx={[
                theme => ({...panelSx(theme), pointerEvents: "none", minWidth, maxWidth: 320}),
                ...(Array.isArray(sx) ? sx : sx ? [sx] : [])
            ]}
        >
            {children}
        </Paper>
    );
}

interface TooltipRowProps {
    label: string;
    value: string;
    color?: string;
    swatch?: string;
}

export function TooltipRow({label, value, color, swatch}: TooltipRowProps) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                gap: 2,
                minHeight: "20px"
            }}
        >
            <Box sx={{display: "flex", alignItems: "center", gap: 1, minWidth: 0}}>
                {swatch != null && (
                    <Box sx={{width: 8, height: 8, borderRadius: "2px", bgcolor: swatch, flexShrink: 0}}/>
                )}

                <Typography component={"span"} sx={{fontSize: 13, color: "text.secondary"}} noWrap>
                    {label}
                </Typography>
            </Box>

            <Typography
                component={"span"}
                sx={{
                    fontSize: 13,
                    fontWeight: color == null ? 600 : 700,
                    color: color ?? "text.primary",
                    fontVariantNumeric: "tabular-nums"
                }}
                noWrap
            >
                {value}
            </Typography>
        </Box>
    );
}

interface TooltipTitleProps {
    title: string;
    subtitle?: string;
}

export function TooltipTitle({title, subtitle}: TooltipTitleProps) {
    return (
        <React.Fragment>
            <Box sx={{px: 0.25, pb: 0.75}}>
                <Typography sx={{color: "text.primary", fontSize: 13, lineHeight: 1.4, fontWeight: 700}}>
                    {title}
                </Typography>

                {subtitle != null && (
                    <Typography sx={{color: "text.secondary", fontSize: 13, lineHeight: 1.4}}>
                        {subtitle}
                    </Typography>
                )}
            </Box>

            <Divider sx={{mb: 0.75}}/>
        </React.Fragment>
    );
}

interface TooltipDeltaProps {
    label: string;
    value: number | null | undefined;
    format: (value: number) => string;
    lowerIsBetter?: boolean;
    swatch?: string;
}

export function TooltipDelta({label, value, format, lowerIsBetter, swatch}: TooltipDeltaProps) {
    if (value == null) {
        return <TooltipRow swatch={swatch} label={label} value={"—"}/>;
    }

    const {text, direction} = signed(value, format);

    return <TooltipRow swatch={swatch} label={label} value={text} color={directionColor(direction, lowerIsBetter)}/>;
}
